/**
 * /release-notes — SEO page for the release cluster: "release notes",
 * "what are release notes", "product release notes", "software release
 * notes" (~709 aggregate, KD 8–15, strong fit). Distinct from the existing
 * /release-plan-template (plan = before shipping; notes = after). Defines
 * release notes, separates them from changelog and release plan, hands over
 * a template, then the Draftlize turn: release notes should say what shipped
 * and why, but they drift from the spec/decisions they describe.
 *
 * English-only + inline. Chinese variant in ReleaseNotesZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ReleaseNotes() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/release-notes"
      documentTitle="Release Notes: What They Are + Template · Draftlize"
      documentDescription="What release notes are, how they differ from a changelog and a release plan, what to include, and a template — plus how to keep them tied to what actually shipped."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Release notes</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            What are release notes?<br/>
            <em>And a template that stays true to what shipped.</em>
          </h1>
          <p className="lede">
            <em>Release notes</em> are the human-readable summary of what changed in a release — the new features, the fixes, the breaking changes — written for the people who use the product, not the people who built it. This page tells you plainly what release notes are, how they differ from a changelog and a release plan, what a good set includes, and hands you a template. Then the honest part: release notes are written once at ship time and immediately start disagreeing with the spec and decisions they describe, because nothing connects them back.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what are release notes?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Release notes are a user-facing announcement of what a release delivers, in plain language: what&apos;s new, what&apos;s improved, what&apos;s fixed, and anything that requires action (a breaking change, a migration). The job is to let someone decide &quot;do I care about this update, and do I need to do anything?&quot; without reading a commit log. Good <em>product release notes</em> lead with value (&quot;export threads to PDF&quot;), not implementation (&quot;added a PDF rendering service&quot;).
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            They&apos;re distinct from two things people conflate them with. A <strong>changelog</strong> is the terse, often developer-facing running list of every change (frequently auto-generated). A <a href="/release-plan-template" style={{ color: 'var(--amber)' }}>release plan</a> is the <em>forward-looking</em> document — what will ship, when, with what rollout. Release notes are the <em>backward-looking</em> announcement once it has.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Don&apos;t confuse them</span>
            <h2 className="serif">Release notes vs changelog <em>vs release plan.</em></h2>
          </div>
          <p>Three artifacts around a release, easy to blur. The difference is audience and direction in time.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>Artifact</th>
              <th>For whom</th>
              <th>Direction</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Release notes</td><td className="yes">Users / customers</td><td>Backward — what shipped</td></tr>
            <tr><td>Changelog</td><td className="partial">Developers, terse</td><td>Backward — every change</td></tr>
            <tr><td>Release plan</td><td className="partial">Internal team</td><td>Forward — what will ship</td></tr>
          </tbody>
        </table>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">What to include. <em>Copy it anywhere.</em></h2>
          </div>
          <p>This is the whole template — it works in an in-app changelog, an email, or a docs page. The next section is about what changes when each note links back to the spec and decisions it describes.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Version & date', hint: 'Header', body: 'The release version (or name) and the date it shipped. The anchor everything else hangs off, and what a user references when they report something on "the version from last Tuesday."' },
            { k: 'Summary / highlights', hint: 'Lead with value', body: 'One or two lines on what this release is about, framed as outcomes. If a reader stops after this, they should still know whether the update matters to them.' },
            { k: 'New features', hint: 'What\'s new', body: 'Each new capability in user terms — what it lets them do, not how it\'s built. A line or two each, linking to deeper docs where it helps.' },
            { k: 'Improvements', hint: 'What\'s better', body: 'Enhancements to existing behavior — faster, clearer, more reliable. The "we heard you" section; tie these to the feedback or requests that prompted them where you can.' },
            { k: 'Fixes', hint: 'What\'s repaired', body: 'Notable bugs resolved, in plain language. You don\'t list every patch — you list the ones a user noticed or asked about.' },
            { k: 'Breaking changes / actions', hint: 'Do something', body: 'Anything that changes behavior they depend on, or requires a migration, a re-auth, an updated integration. The single most important section when it applies — bury it and you generate support tickets.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          That&apos;s the template. Below: why release notes drift from what actually shipped — and how we keep them tied to it.
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Notes describe a release. <em>The release keeps moving.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Release notes are a snapshot of what a release was supposed to be at ship time. But the spec a note describes, and the decisions under it, keep changing — and nothing connects the note back. Draftlize keeps the thread.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each note links to the spec it announces</h3>
            <p>In Draftlize a release note isn&apos;t free text typed from memory — it&apos;s generated from the cards that actually shipped: the features, the fixes, the decisions behind them. The note links to the spec it describes, so &quot;what did this release actually change&quot; is a graph query, not an archaeology dig through tickets.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A late scope cut, and the note flags stale</h3>
            <p>A feature gets pulled the day before ship, or a decision reverses post-release. Because the note links to the spec, it auto-flags stale instead of confidently announcing something that didn&apos;t ship — the embarrassing release-note bug that no spreadsheet catches.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent drafts notes from what shipped</h3>
            <p>Ask Claude Code or Cursor to draft the next release notes over MCP and it reads the cards that actually changed in this release — so the draft starts from the real diff of decisions and specs, not a half-remembered list someone reconstructs at 5pm on ship day.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Release notes are written once and never updated. The trouble is the release they describe is still changing right up to ship.
        </blockquote>
        <cite>Keep the format. Generate the notes from what actually shipped.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between release notes and a changelog?', a: 'Release notes are user-facing and curated — they explain what a release means in plain language, leading with value. A changelog is a terse, often developer-facing and frequently auto-generated running list of every change. Release notes are a story for users; a changelog is a record for builders.' },
            { q: 'What is the difference between release notes and a release plan?', a: 'A release plan is forward-looking — what will ship, when, with what rollout and rollback. Release notes are backward-looking — what did ship, written once it has. The plan is internal and operational; the notes are external and announcement-shaped.' },
            { q: 'What should good release notes include?', a: 'Version and date, a value-led summary, new features in user terms, improvements, notable fixes, and — most important when it applies — breaking changes or required actions. Lead with what the update lets someone do, not how it was built.' },
            { q: 'How often should you publish release notes?', a: 'Match your release cadence. Continuous-delivery teams often batch user-visible changes into a periodic digest so users are not pinged on every deploy; scheduled releases publish notes per release. The rule is consistency, so users learn where to look.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Generate notes from what shipped <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or let Draftlize draft each release&apos;s notes from the cards that actually changed — linked to the specs they describe, so a late scope cut flags the note instead of shipping a lie.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
